import { FC, Fragment, useEffect, useMemo, useState } from "react";
import { Checkbox, Dialog, DialogPanel, Transition } from "@headlessui/react";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { StripeCardElementOptions } from "@stripe/stripe-js";
import Input from "shared/Input/Input";
import { useNavigate, useNavigationType } from "react-router-dom";
import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import PrivacyPolicy from "containers/PageHome/PrivacyPolicy";
import { globalJson } from "global/global_json";
import { useData } from "data/data-provider";
import { getRentalAgreementContent } from "api/rental-agreement/rental-agreement";
import Cancellation from "containers/PageHome/Cancellation";
import RentalAgreement from "containers/PageHome/RentalAgreement";
import { PaymentDone } from "api/stripe/stripe";
import moment from "moment";
import SquarePaymentForm from "./SquarePaymentForm";
import { getSquareDetails } from "api/square/square";
import BackRouteButton from "shared/BackRouteButton/BackRouteButton";
import Invoice from "designs/Common/(car-search-form)/invoice";
import { resolvePolicyCheckboxCopy } from "utils/policyCheckboxCopy";
import { sanitizeHtml } from "utils/sanitizeHtml";
import { getAgreementPdfBase } from "utils/agreement-url";
import { COUNTRIES, isValidCountryCode } from "utils/countries";
import { getStripeAvailability } from "utils/stripeAvailability";
import { formatPrice } from "utils/convertNumbThousand";
import { trackEvent } from "analytics/posthog";

const paymentUnavailableNotice = (
  <div className="mt-8 pt-8 border-t border-neutral-200">
    <div className="text-yellow-600 dark:text-yellow-500 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
      <p className="font-semibold">Payment system unavailable</p>
      <p className="text-sm mt-1">
        Our payment system is temporarily unavailable. Please try again later
        or contact support.
      </p>
    </div>
  </div>
);

export interface CheckOutPagePageMainProps {
  className?: string;
}

const CheckOutPagePageMain: FC<CheckOutPagePageMainProps> = ({
  className = "",
}) => {
  const {
    user,
    startDate,
    endDate,
    pickupLocation,
    dropOffLocation,
    setCouponResponse,
    checkCouponResponseLoading,
    selectedCar,
    searchedPickupLoction,
    searchedDropoffLocation,
    bookingdetails,
    callCouponAPI,
    saveCard,
    couponResponse,
    dropoffLocationType,
    callremoveCouponAPI,
    ourCompany,
    companyDetails,
  } = useData();
  const [inputValue, setInputValue] = useState("");
  let [isOpen, setIsOpen] = useState(false);
  let [isAgreementOpen, setIsAgreementOpen] = useState(false);
  let [isTermOpen, setIsTermOpen] = useState(false);
  let [isCancellationOpen, setIsCancellationOpen] = useState(false);
  let [rentalAgreementChecked, setRentalAgreementChecked] = useState(false);
  let [loading, setLoading] = useState(false);
  let [checkoutLoading, setCheckoutLoading] = useState(false);
  let [privacyPolicyCheckedChecked, setPrivacyPolicyChecked] = useState(false);
  let [termsandconditionChecked, setTermsandConditionChecked] = useState(false);
  let [agreementContent, setAgreementContent] = useState<any>([]);
  const [privacyPolicy, setPrivacyPolicy] = useState<any>([]);
  const [cancellationPolicy, setCancellationPolicy] = useState<any>([]);
  const [squareDetails, setSquareDetails] = useState<any>();
  const stripe = useStripe();
  const elements = useElements();
  // Did Stripe.js actually come up for this host? Resolved in index.tsx at
  // boot; "loading" until it settles, so the card fields still render
  // optimistically on a normal page load rather than flashing an error.
  // (1NOW-1608)
  const [stripeStatus, setStripeStatus] = useState<
    "loading" | "ready" | "unavailable"
  >("loading");
  const navigate = useNavigate();
  const [isCustomAgreementOpen, setIsCustomAgreementOpen] = useState(false);
  const [isRentalAgreementPdfOpen, setIsRentalAgreementPdfOpen] =
    useState(false);
  const [rentalAgreementPdfUrl, setRentalAgreementPdfUrl] = useState("");
  let [rentalAgreementPdfChecked, setRentalAgreementPdfChecked] =
    useState(false);

  // Billing address (Stripe billing_details)
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [stateField, setStateField] = useState("");

  const policyCheckboxCopy = useMemo(
    () => resolvePolicyCheckboxCopy(companyDetails),
    [companyDetails]
  );

  // 1NOW-649 — "pay in full" vs "pay deposit now" when the host enabled a
  // partial-payment policy (surfaced on bookingdetails.partial_payment).
  const partialPayment = bookingdetails?.partial_payment;
  const partialEnabled = !!partialPayment?.enabled;
  const [paymentChoice, setPaymentChoice] = useState<"full" | "deposit">(
    "full"
  );

  const allAccepted =
    rentalAgreementPdfChecked &&
    termsandconditionChecked &&
    privacyPolicyCheckedChecked;

  const toggleAcceptAll = (checked: boolean) => {
    setRentalAgreementPdfChecked(checked);
    setRentalAgreementChecked(checked);
    setTermsandConditionChecked(checked);
    setPrivacyPolicyChecked(checked);
    if (checked) toast.success("All policies accepted");
  };

  const navigationType = useNavigationType();

  useEffect(() => {
    if (navigationType === "POP") {
      navigate("/", { replace: true });
    }
  }, [navigationType, navigate]);

  useEffect(() => {
    let active = true;
    getStripeAvailability().then((ok) => {
      if (active) setStripeStatus(ok ? "ready" : "unavailable");
    });
    return () => {
      active = false;
    };
  }, []);

  // Funnel step: customer reached checkout. Counted as "Started booking"
  // (scoped by company_id) in the admin monthly-review dashboard.
  useEffect(() => {
    trackEvent("booking_started", {
      vehicle_id: selectedCar?.id,
      booking_id: bookingdetails?.id,
      // Ex-deposit, i.e. the revenue -- the security deposit is refunded and
      // must not inflate what the ad platforms optimise on.
      value: bookingdetails?.final_price_without_security,
      currency: ourCompany?.currency,
    });
  }, []);

  const handlePaymentSuccess = (data: any) => {
    // Funnel step: "Completed booking" (Square rail).
    trackEvent("booking_completed", {
      vehicle_id: selectedCar?.id,
      booking_id: bookingdetails?.id,
      payment_method: "square",
      value: bookingdetails?.final_price_without_security,
      currency: ourCompany?.currency,
    });
    // 1NOW-10711: the trip amount can be captured while the deposit on the
    // same card is declined. Say exactly that, instead of a plain "success"
    // that hides an outstanding deposit -- or, worse, a "failed" that used to
    // make renters pay the trip amount a second time.
    const depositNotice: string | null = data?.deposit_error
      ? `Your payment of $${Number(data.amount_charged ?? 0).toFixed(2)} went through, ` +
        `but the $${Number(data.deposit_amount ?? 0).toFixed(2)} security deposit could not be charged ` +
        `(${data.deposit_error}). Your booking is confirmed; the host will follow up to collect the deposit.`
      : null;
    if (depositNotice) {
      toast(depositNotice, { duration: 12000, icon: "⚠️" });
    } else {
      toast.success("Payment completed successfully!");
    }
    navigate(`/payment-success`, { state: { depositNotice } });
  };

  const handlePaymentError = (error: any) => {
    toast.error(error);
  };

  const cardElementOptions: StripeCardElementOptions = {
    style: {
      base: {
        color: "#32325d",
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        fontSmoothing: "antialiased",
        fontSize: "16px",
        "::placeholder": {
          color: "#aab7c4",
        },
      },
      invalid: {
        color: "#fa755a",
        iconColor: "#fa755a",
      },
    },
  };

  useEffect(() => {
    const openRentalAgreement = async () => {
      if (bookingdetails && globalJson && bookingdetails?.customer?.email) {
        /* The agreement key is the booking's uuid. It used to be
           `{pk}b6cr4{companyId}b6cr4{base64(email)}`, but the backend only ever
           read the first segment — the company id was logged and never
           checked, and the email was ignored outright — so a guessed integer
           opened any operator's agreement (1NOW-10707). Both dead segments are
           gone with it. */
        const path = bookingdetails?.unique_identifier;

        try {
          const response = await getRentalAgreementContent(path);
          if (response?.data) {
            setAgreementContent(response.data);
            setPrivacyPolicy(response.data.content.privacy_policy);
            setCancellationPolicy(response.data.content.cancellation_policy);
          }
        } catch (error) {
          console.error("Error fetching rental agreement content:", error);
        }

        // setUrl(`https://dev.1now.app/pdf?agreement=${path}`);
      }
    };

    openRentalAgreement();
  }, [bookingdetails, globalJson, user]); // Add dependencies to avoid unnecessary re-renders

  useEffect(() => {
    callFetchSquareDetails();
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    if (!pickupLocation) {
      navigate("/");
    }
  }, [pickupLocation]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const openRentalAgreementModal = () => {
    /* The agreement key is the booking's uuid. The old
       `{pk}b6cr4{companyId}b6cr4{base64(email)}` salt read like a secret
       but was not — the backend only ever looked at the first segment, so
       a guessed integer opened any operator's agreement (1NOW-10707). */
    const path = bookingdetails?.unique_identifier;

    // Keyed off serverURL rather than REACT_APP_ENV, which has no "qa" value
    // and used to fall through to the prod PDF host on QA, bouncing the
    // renter to the 1NOW sign-in page instead of the agreement. (1NOW-1239)
    const url = getAgreementPdfBase();

    setRentalAgreementPdfUrl(`${url}${path}`);
    setIsRentalAgreementPdfOpen(true);
  };

  const handleAgreementOpen = (isOpen: boolean) => {
    if (ourCompany?.default_agreement === "custom") {
      /* The agreement key is the booking's uuid. It used to be
         `{pk}b6cr4{companyId}b6cr4{base64(email)}`, but the backend only ever
         read the first segment — the company id was logged and never
         checked, and the email was ignored outright — so a guessed integer
         opened any operator's agreement (1NOW-10707). Both dead segments are
         gone with it. */
      const path = bookingdetails?.unique_identifier;

      const url = getAgreementPdfBase();

      const iframeUrl = `${url}${path}`;
      setAgreementContent({ ...agreementContent, iframeUrl });
      setIsCustomAgreementOpen(isOpen); // open the custom modal
    } else {
      setIsAgreementOpen(isOpen);
      // if (isOpen) {
      //   toast(
      //     "Please review the Rental Agreement carefully before proceeding.",
      //     {
      //       style: {
      //         background: "#fff3cd",
      //         color: "#856404",
      //       },
      //     }
      //   );
      // }
    }
  };

  const handleCouponSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();
    callCouponAPI(inputValue, bookingdetails?.id);
    setInputValue("");
  };

  const callCheckout = async () => {
    setCheckoutLoading(true);
    setCheckoutLoading(false);
  };

  useEffect(() => {
    if (inputValue == "") {
      setCouponResponse(null);
    }
  }, [inputValue]);

  // Handle payment submission
  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (rentalAgreementPdfChecked !== true) {
      toast.error("Please review and accept the Rental Agreement");
      return;
    }
    if (termsandconditionChecked !== true) {
      toast.error("Please review and accept the Terms and Conditions");
      return;
    }
    if (privacyPolicyCheckedChecked !== true) {
      toast.error("Please agree to Privacy & Cancellation Policies");
      return;
    }
    // Stripe.js is still loading, or it failed and the notice hasn't
    // replaced the form yet. Either way the click used to be a silent
    // no-op. (1NOW-1608)
    if (!stripe || !elements) {
      toast.error(
        "The payment form hasn't finished loading. Please refresh the page, or contact support if this persists."
      );
      return;
    }

    // A length check is not a validity check: the old free-text field
    // silently truncated "United States" to "Un" -> "UN", which passed
    // `length === 2` and was rejected by Stripe as an unknown country.
    // (1NOW-10466)
    if (!isValidCountryCode(country)) {
      toast.error("Please select your billing country.");
      return;
    }

    setLoading(true);

    const cardNumberElement = elements.getElement(CardNumberElement);
    if (!cardNumberElement) {
      toast.error("Card element not found.");
      setLoading(false);
      return;
    }

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: cardNumberElement,
      billing_details: {
        address: {
          line1: address,
          city: city,
          country: country.trim().toUpperCase(),
          postal_code: postalCode,
          state: stateField,
        },
      },
    });
    if (error) {
      toast.error("Payment failed: " + error.message);
      setLoading(false);
      return;
    } else {
      payDone(paymentMethod.id, partialEnabled && paymentChoice === "deposit");
    }
  };

  const payDone = async (paymentMethodId: any, isPartial: boolean = false) => {
    setLoading(true);
    try {
      await saveCard(paymentMethodId);
    } catch (error) {
      console.error("Error saving card", error);
    }
    try {
      const response = await PaymentDone(
        paymentMethodId,
        bookingdetails.id,
        isPartial
      );
      if (response?.error) {
        toast.error(response?.error);
      } else {
        // Funnel step: "Completed booking" (Stripe rail).
        trackEvent("booking_completed", {
          vehicle_id: selectedCar?.id,
          booking_id: bookingdetails?.id,
          payment_method: "stripe",
          // What was actually charged: the deposit option (1NOW-649) takes
          // `deposit_due_now`, not the full price.
          value: isPartial
            ? partialPayment?.deposit_due_now
            : bookingdetails?.final_price_without_security,
          currency: ourCompany?.currency,
        });
        toast.success("Payment completed successfully!");
        navigate(`/payment-success`);
      }
    } catch (error: any) {
      console.error("Payment Error", error);
    } finally {
      setLoading(false);
    }
  };
  const callFetchSquareDetails = async () => {
    setLoading(true);
    try {
      const resp = await getSquareDetails();
      setSquareDetails(resp);
    } catch (error: any) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  function close() {
    setIsOpen(false);
  }

  const renderSidebar = () => {
    // One string for both the visible text and the accessible name, so the
    // button keeps its name while it shows the loading spinner.
    const payLabel =
      partialEnabled && paymentChoice === "deposit"
        ? `Pay deposit $${formatPrice(partialPayment?.deposit_due_now)} and Book`
        : "Pay and Book";
    return (
      <div className="w-full flex flex-col sm:rounded-2xl lg:border border-neutral-200 dark:border-neutral-700 px-0 sm:p-6 xl:p-8 mt-4">
        <div className="flex flex-col sm:flex-row sm:items-center">
          <div className="flex-shrink-0 w-full"></div>
        </div>
        <div className="flex flex-col space-y-4">
          <div className="w-full mx-auto">
            {bookingdetails?.invoice && (
              <Invoice
                invoice={bookingdetails?.invoice}
                carName={bookingdetails?.fleet?.name}
                partialPayment={partialPayment}
                onRemoveCoupon={
                  couponResponse !== null
                    ? () => {
                        setCouponResponse(null);
                        callremoveCouponAPI(
                          couponResponse?.coupon_info?.name,
                          bookingdetails?.id
                        );
                      }
                    : undefined
                }
                promoSlot={
                  couponResponse !== null ? null : (
                    <form
                      onSubmit={handleCouponSubmit}
                      className="relative w-full"
                    >
                      <Input
                        value={inputValue}
                        onChange={handleInputChange}
                        required
                        aria-required
                        placeholder="Promo Code"
                        type="text"
                        rounded="rounded-md w-full"
                        className="pr-[7rem]"
                      />
                      <button
                        type="submit"
                        className="absolute transform top-1/2 -translate-y-1/2 right-[5px] cursor-pointer ttnc-ButtonCircle flex items-center justify-center rounded-md !leading-none whitespace-nowrap disabled:bg-opacity-70 bg-primary-6000 hover:bg-primary-700 text-neutral-50 px-8 py-3"
                      >
                        {checkCouponResponseLoading ? (
                          <div className="flex justify-center items-center col-span-full">
                            <div className="spinner-border animate-spin inline-block w-6 h-6 border-4 rounded-full border-t-transparent border-neutral-200"></div>
                          </div>
                        ) : (
                          "Apply"
                        )}
                      </button>
                    </form>
                  )
                }
              />
            )}

            {!ourCompany?.default_payment ? (
              paymentUnavailableNotice
            ) : ourCompany?.default_payment === "square" ? (
              squareDetails?.application_id ? (
                <SquarePaymentForm
                  squareDetails={squareDetails}
                  onPaymentSuccess={handlePaymentSuccess}
                  onPaymentError={handlePaymentError}
                  amount={bookingdetails?.final_price_without_security}
                  bookingId={bookingdetails?.id}
                  renterId={bookingdetails?.customer?.id || ""}
                  termsandconditionChecked={termsandconditionChecked}
                  rentalAgreementChecked={rentalAgreementChecked}
                  privacyPolicyCheckedChecked={privacyPolicyCheckedChecked}
                />
              ) : null
            ) : stripeStatus === "unavailable" ? (
              // Stripe.js never initialised for this host, so the card
              // fields would render as empty unclickable boxes above a "Pay
              // and Book" button that silently does nothing. Say so instead.
              // (1NOW-1608)
              paymentUnavailableNotice
            ) : (
              <form onSubmit={handleSubmit}>
                {partialEnabled && (
                  <div className="mt-8 pt-8">
                    <span className="text-lg text-gray-700 dark:text-gray-300 font-semibold">
                      Payment Option
                    </span>
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setPaymentChoice("full")}
                        className={`text-left p-4 rounded-md border transition-colors ${
                          paymentChoice === "full"
                            ? "border-primary-6000 ring-1 ring-primary-6000"
                            : "border-neutral-300"
                        }`}
                      >
                        <span className="block font-semibold text-text-color">
                          Pay in full
                        </span>
                        <span className="block text-sm text-text-color/70 mt-1">
                          $
                          {formatPrice(
                            bookingdetails?.final_price_without_security
                          )}{" "}
                          now
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setPaymentChoice("deposit")}
                        className={`text-left p-4 rounded-md border transition-colors ${
                          paymentChoice === "deposit"
                            ? "border-primary-6000 ring-1 ring-primary-6000"
                            : "border-neutral-300"
                        }`}
                      >
                        <span className="block font-semibold text-text-color">
                          Pay deposit now
                        </span>
                        <span className="block text-sm text-text-color/70 mt-1">
                          ${formatPrice(partialPayment?.deposit_due_now)} now ·
                          ${formatPrice(partialPayment?.balance_due_later)}{" "}
                          balance
                          {partialPayment?.balance_due_date
                            ? ` due ${moment(
                                partialPayment?.balance_due_date
                              ).format("MMM D, YYYY")}`
                            : ""}
                        </span>
                      </button>
                    </div>
                  </div>
                )}
                <div className="mt-8 pt-8">
                  <span className="text-lg text-gray-700 dark:text-gray-300 font-semibold">
                    Billing Information
                  </span>
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      type="text"
                      placeholder="Address"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      className="md:col-span-2 rounded-md border !border-neutral-300"
                      minLength={5}
                      maxLength={100}
                      required
                    />
                    <Input
                      type="text"
                      placeholder="City"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      minLength={2}
                      maxLength={50}
                      className="rounded-md border !border-neutral-300"
                      pattern="[A-Za-z\s]+"
                      title="City should only contain letters and spaces"
                      required
                    />
                    <Input
                      type="text"
                      placeholder="State"
                      value={stateField}
                      onChange={(e) => setStateField(e.target.value)}
                      minLength={2}
                      maxLength={50}
                      className="rounded-md border !border-neutral-300"
                      pattern="[A-Za-z\s]+"
                      title="State should only contain letters and spaces"
                      required
                    />
                    {/* A select, not free text: `maxLength={2}` quietly kept
                        the first two letters of whatever was typed, so
                        "United States" became "UN" and Stripe refused the
                        payment. Picking from real ISO 3166-1 alpha-2 codes
                        makes an invalid country unreachable. (1NOW-10466) */}
                    <div className="relative">
                      <select
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                        className="block h-11 w-full appearance-none rounded-md border !border-neutral-300 bg-bg-color bg-none pl-4 pr-10 text-sm font-normal text-text-color focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                        title="Select your billing country"
                        required
                      >
                        <option value="" className="bg-bg-color text-text-color">
                          Select country
                        </option>
                        {COUNTRIES.map((c) => (
                          <option
                            key={c.code}
                            value={c.code}
                            className="bg-bg-color text-text-color"
                          >
                            {c.name} ({c.code})
                          </option>
                        ))}
                      </select>
                      <svg
                        className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-color"
                        viewBox="0 0 20 20"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M6 8l4 4 4-4" />
                      </svg>
                    </div>
                    <Input
                      type="text"
                      placeholder="Postal Code"
                      value={postalCode}
                      onChange={(e) => setPostalCode(e.target.value)}
                      minLength={3}
                      maxLength={10}
                      className="rounded-md border !border-neutral-300"
                      pattern="[A-Za-z0-9\s-]+"
                      title="Enter valid postal code"
                      required
                    />
                  </div>
                </div>
                <div className="mt-8 pt-8 border-t border-neutral-200">
                  <span className="text-lg text-gray-700 dark:text-gray-300 font-semibold">
                    Payment Information
                  </span>
                  <div className="p-3 mt-4 border rounded-md">
                    <CardNumberElement
                      id="card-number-element"
                      options={cardElementOptions}
                      className="w-full"
                    />
                  </div>

                  <div className="flex mt-4 space-x-4">
                    <div className="flex-1 border p-3 rounded-md">
                      <CardExpiryElement
                        id="card-expiry-element"
                        options={cardElementOptions}
                        className="w-full"
                      />
                    </div>

                    <div className="flex-1 border p-3 rounded-md">
                      <CardCvcElement
                        id="card-cvc-element"
                        options={cardElementOptions}
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>
                <ButtonPrimary
                  className="w-full mt-4 rounded-lg"
                  type="submit"
                  disabled={loading}
                  aria-label={payLabel}
                >
                  {loading ? (
                    <div className="flex justify-center items-center col-span-full">
                      <div className="spinner-border animate-spin inline-block w-6 h-6 border-4 rounded-full border-t-transparent border-gray-300"></div>
                    </div>
                  ) : (
                    payLabel
                  )}
                </ButtonPrimary>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  };
console.log("bookingdetails", bookingdetails);
  const mainSecion = () => {
    return (
      <div className="flex flex-col  sm:rounded-2xl sm:border border-neutral-200 dark:border-neutral-700 space-y-8 px-0 sm:p-6 xl:p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className=" w-full aspect-w-4 aspect-h-2 rounded-2xl overflow-hidden">
            <img
              alt=""
              className="absolute inset-0 object-cover w-full"
              sizes="200px"
              src={
                (bookingdetails?.fleet?.fleet_photos?.length ?? 0) > 0
                  ? `https://fleet-management-images-upload-be.s3.amazonaws.com/${bookingdetails?.fleet?.fleet_photos[0].photo_url}`
                  : "https://tenant.fleetwire.io/images/vehicle-placeholder.jpg"
              }
            />
          </div>
          <div>
            <div className="mt-4 max-w-xs">
              <p className="font-bold text-lg">Pick up Details</p>
              <p>
                {" "}
                {searchedPickupLoction
                  ? searchedPickupLoction
                  : pickupLocation?.name}
              </p>
              <div className="flex justify-between">
                <span>{moment(startDate).format("ddd, D MMM YYYY")}</span>
                <span>{moment(startDate).format("hh:mm A")}</span>
              </div>
            </div>
            <div className="mt-4 max-w-xs">
              <p className="font-bold text-lg">Drop off Details</p>
              <p>
                {" "}
                {dropoffLocationType === false &&
                searchedDropoffLocation.length > 0
                  ? searchedDropoffLocation
                  : dropOffLocation?.name
                  ? dropOffLocation?.name
                  : dropoffLocationType === true &&
                    searchedPickupLoction.length > 0
                  ? searchedPickupLoction
                  : pickupLocation?.name}
              </p>
              <div className="flex justify-between">
                <span>{moment(endDate).format("ddd, D MMM YYYY")}</span>
                <span>{moment(endDate).format("hh:mm A")}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const termsSection = () => {
    return (
      <div className="w-full flex flex-col sm:rounded-2xl sm:border border-neutral-200 dark:border-neutral-700 space-y-8 px-0 sm:p-6 xl:p-8 mt-4">
        <div>
          <h3 className="text-2xl font-bold text-[#111827]">
            Terms & Conditions
          </h3>

          <div className="flex gap-2 mt-6 p-4 border border-neutral-200 dark:border-neutral-700 rounded-3xl overflow-hidden z-10">
            <Checkbox
              checked={rentalAgreementPdfChecked}
              onChange={(checked) => {
                setRentalAgreementPdfChecked(checked);
                setRentalAgreementChecked(checked);
              }}
              className="group size-7 rounded-md p-1 ring-1 ring-primary-200 data-[checked]:bg-primary-200 data-[checked]:bg-opacity-20 ring-inset  mr-4"
            >
              <CheckIcon
                className={`size-5 stroke-primary-200 ${
                  rentalAgreementPdfChecked ? "visible" : "invisible"
                }`}
              />
            </Checkbox>
            <div>
              <div
                className="policy-checkbox-copy text-text-color text-lg font-medium "
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(policyCheckboxCopy.rentalAgreementText),
                }}
              />
              <div
                className="text-gray-800 dark:text-gray-200 text-xl font-bold cursor-pointer underline"
                onClick={() => {
                  openRentalAgreementModal();
                }}
              >
                Rental Agreement{" "}
              </div>
            </div>
          </div>
          <div className="flex gap-2 mt-6 p-4 border border-neutral-200 dark:border-[#E5E7EB] rounded-3xl overflow-hidden z-10">
            <Checkbox
              checked={termsandconditionChecked}
              onChange={(checked) => {
                setTermsandConditionChecked(checked);
              }}
              className="group size-7 rounded-md p-1 ring-1 ring-primary-200 data-[checked]:bg-primary-200 data-[checked]:bg-opacity-20 ring-inset  mr-4"
            >
              <CheckIcon
                className={`size-5 stroke-primary-200 ${
                  termsandconditionChecked ? "visible" : "invisible"
                }`}
              />
            </Checkbox>
            <div>
              <div
                className="policy-checkbox-copy text-[#121212] text-lg font-medium "
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(policyCheckboxCopy.termsConditionsText),
                }}
              />
              <div
                className="text-gray-800 dark:text-gray-200 text-xl font-bold cursor-pointer underline"
                onClick={() => {
                  handleAgreementOpen(true);
                }}
              >
                Terms and Conditions
              </div>
            </div>
          </div>
          <div className="flex gap-2 mt-6 p-4 border border-neutral-200 dark:border-neutral-700 rounded-3xl overflow-hidden z-10">
            <Checkbox
              checked={privacyPolicyCheckedChecked}
              onChange={(checked) => {
                setPrivacyPolicyChecked(checked);
              }}
              className="group size-7 rounded-md p-1 ring-1 ring-primary-200 data-[checked]:bg-primary-200 data-[checked]:bg-opacity-20 ring-inset  mr-4"
            >
              <CheckIcon
                className={`size-5 stroke-primary-200 ${
                  privacyPolicyCheckedChecked ? "visible" : "invisible"
                }`}
              />
            </Checkbox>
            <div>
              <div
                className="policy-checkbox-copy text-text-color text-lg font-medium "
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(policyCheckboxCopy.privacyCancellationText),
                }}
              />

              <span
                className="text-gray-800 text-xl font-bold cursor-pointer underline"
                onClick={() => {
                  setIsTermOpen(true);
                }}
              >
                Privacy Policy
              </span>
              <span className="text-gray-800 text-xl font-bold cursor-pointer mx-2">
                &
              </span>
              <span
                className="text-gray-800 text-xl font-bold cursor-pointer underline"
                onClick={() => {
                  setIsCancellationOpen(true);
                }}
              >
                Cancellation Policy
              </span>
            </div>
          </div>

          <div className="flex gap-2 mt-6 p-4 border border-neutral-200 dark:border-[#E5E7EB] rounded-3xl overflow-hidden items-center">
            <Checkbox
              checked={allAccepted}
              onChange={(checked) => toggleAcceptAll(checked)}
              className="group size-7 rounded-md p-1 ring-1 ring-primary-200 data-[checked]:bg-primary-200 data-[checked]:bg-opacity-20 ring-inset mr-2"
            >
              <CheckIcon
                className={`size-5 stroke-primary-200 ${
                  allAccepted ? "visible" : "invisible"
                }`}
              />
            </Checkbox>
            <div className="text-[#121212]">
              <p className="font-bold">Accept all policies</p>
              <p className="text-[#121212] text-sm">
                By proceeding you agree to the{" "}
                <button
                  type="button"
                  onClick={() => openRentalAgreementModal()}
                  className="underline text-gray-800 dark:text-white"
                >
                  Rental Agreement
                </button>
                ,{" "}
                <button
                  type="button"
                  onClick={() => handleAgreementOpen(true)}
                  className="underline text-gray-800 dark:text-white"
                >
                  Terms and Conditions
                </button>
                , and the{" "}
                <button
                  type="button"
                  onClick={() => setIsTermOpen(true)}
                  className="underline text-gray-800 dark:text-white"
                >
                  Privacy & Cancellation Policies
                </button>
                .
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /**
   * 1NOW-9077 — every field on this page reads through `bookingdetails?.…`,
   * so with no booking in state the whole thing renders as an empty frame:
   * no car, no dates, no price, no explanation. That happened after a failed
   * reserve, and still happens on a refresh or a direct link, because the
   * booking lives in memory and does not survive either.
   *
   * A booking we can actually render has an id. Anything else gets told what
   * went wrong and how to get back, rather than a blank page.
   */
  const hasBooking = Boolean(bookingdetails?.id);

  if (!hasBooking) {
    // A POP navigation — back button, or the URL opened directly — is
    // already being sent home by the effect above. Render nothing for that
    // frame rather than flashing a message the renter has no time to read.
    if (navigationType === "POP") return null;

    return (
      <div className={`nc-CheckOutPagePageMain ${className}`}>
        <main className="container mt-11 pb-10">
          <div className="max-w-2xl mx-auto">
            <BackRouteButton className="mb-6" />
            <div className="mt-8 rounded-2xl border border-neutral-200 px-6 py-12 text-center">
              <p className="text-text-color font-semibold">
                We couldn't start your booking
              </p>
              <p className="text-text-grey text-sm mt-2 max-w-sm mx-auto">
                Nothing has been charged. Please choose your car and dates
                again — if this keeps happening, contact the rental company.
              </p>
              <ButtonPrimary
                className="mt-6"
                onClick={() => navigate("/vehicles")}
              >
                Browse cars
              </ButtonPrimary>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <>
      <div className={`nc-CheckOutPagePageMain ${className}`}>
        <main className="container mt-11 mb-24 lg:mb-32 ">
          <div className="max-w-2xl mx-auto">
            <BackRouteButton className="mb-6" />
            {mainSecion()}
            {termsSection()}
            {renderSidebar()}
          </div>
        </main>
      </div>

      <Transition appear show={isTermOpen} as={Fragment}>
        <Dialog
          as="div"
          className="HeroSearchFormMobile__Dialog relative z-50"
          onClose={() => {
            setIsTermOpen(false);
          }}
          static={true}
        >
          <div className="fixed inset-0 bg-neutral-100 dark:bg-neutral-900">
            <div className="flex h-full">
              <Transition
                as={Fragment}
                enter="ease-out transition-transform"
                enterFrom="opacity-0 translate-y-52"
                enterTo="opacity-100 translate-y-0"
                leave="ease-in transition-transform"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-52"
              >
                <DialogPanel className="relative h-full w-full overflow-hidden flex-1 flex flex-col justify-between ">
                  <>
                    <div className="absolute left-4 top-4">
                      <button
                        className="focus:outline-none focus:ring-0"
                        onClick={close}
                      >
                        <XMarkIcon
                          onClick={() => {
                            setIsTermOpen(false);
                          }}
                          className="w-5 h-5 text-black dark:text-white"
                        />
                      </button>
                    </div>

                    <div className="flex-1 pt-12 p-1 flex flex-col overflow-auto">
                      <div className="flex-1 flex flex-col bg-white dark:bg-neutral-800 z-10">
                        <div className="flex-1 flex flex-col transition-opacity animate-[myblur_0.4s_ease-in-out] overflow-auto">
                          <PrivacyPolicy terms={privacyPolicy} />
                        </div>
                      </div>
                    </div>

                    <div className=" justify-end px-4 py-3 bg-white dark:bg-neutral-900 border-t border-neutral-200 dark:border-neutral-700 flex">
                      <ButtonPrimary
                        sizeClass="px-6 py-3 !rounded-lg"
                        onClick={() => {
                          setPrivacyPolicyChecked(true);
                          setIsTermOpen(false);
                        }}
                      >
                        Accept
                      </ButtonPrimary>
                    </div>
                  </>
                </DialogPanel>
              </Transition>
            </div>
          </div>
        </Dialog>
      </Transition>
      <Transition appear show={isAgreementOpen} as={Fragment}>
        <Dialog
          as="div"
          className="HeroSearchFormMobile__Dialog relative z-50"
          onClose={() => {
            setIsAgreementOpen(false);
          }}
          static={true}
        >
          <div className="fixed inset-0 bg-neutral-100 dark:bg-neutral-900">
            <div className="flex h-full">
              <Transition
                as={Fragment}
                enter="ease-out transition-transform"
                enterFrom="opacity-0 translate-y-52"
                enterTo="opacity-100 translate-y-0"
                leave="ease-in transition-transform"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-52"
              >
                <DialogPanel className="relative h-full w-full overflow-hidden flex-1 flex flex-col justify-between ">
                  <>
                    <div className="absolute left-4 top-4">
                      <button
                        className="focus:outline-none focus:ring-0"
                        onClick={close}
                      >
                        <XMarkIcon
                          onClick={() => {
                            setIsAgreementOpen(false);
                          }}
                          className="w-5 h-5 text-black dark:text-white"
                        />
                      </button>
                    </div>

                    <div className="flex-1 pt-12 p-1 flex flex-col overflow-auto">
                      <div className="flex-1 flex flex-col bg-white dark:bg-neutral-800 z-10">
                        <div className="flex-1 flex flex-col transition-opacity animate-[myblur_0.4s_ease-in-out] overflow-auto">
                          <RentalAgreement data={agreementContent} />
                        </div>
                      </div>
                    </div>
                    <div className=" justify-end px-4 py-3 bg-white dark:bg-neutral-900 border-t border-neutral-200 dark:border-neutral-700 flex">
                      <ButtonPrimary
                        sizeClass="px-6 py-3 !rounded-lg"
                        onClick={() => {
                          setTermsandConditionChecked(true);
                          setIsAgreementOpen(false);
                        }}
                      >
                        Accept
                      </ButtonPrimary>
                    </div>
                  </>
                </DialogPanel>
              </Transition>
            </div>
          </div>
        </Dialog>
      </Transition>
      <Transition appear show={isCancellationOpen} as={Fragment}>
        <Dialog
          as="div"
          className="HeroSearchFormMobile__Dialog relative z-50"
          onClose={() => {
            setIsCancellationOpen(false);
          }}
          static={true}
        >
          <div className="fixed inset-0 bg-neutral-100 dark:bg-neutral-900">
            <div className="flex h-full">
              <Transition
                as={Fragment}
                enter="ease-out transition-transform"
                enterFrom="opacity-0 translate-y-52"
                enterTo="opacity-100 translate-y-0"
                leave="ease-in transition-transform"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-52"
              >
                <DialogPanel className="relative h-full w-full overflow-hidden flex-1 flex flex-col justify-between ">
                  <>
                    <div className="absolute left-4 top-4 z-0">
                      <button
                        className="focus:outline-none focus:ring-0"
                        onClick={close}
                      >
                        <XMarkIcon
                          onClick={() => {
                            setIsCancellationOpen(false);
                          }}
                          className="w-5 h-5 text-black dark:text-white"
                        />
                      </button>
                    </div>

                    <div className="flex-1 pt-12 p-1 flex flex-col overflow-auto">
                      <div className="flex-1 flex flex-col bg-white dark:bg-neutral-800 z-10">
                        <div className="flex-1 flex flex-col transition-opacity animate-[myblur_0.4s_ease-in-out] overflow-auto">
                          <Cancellation terms={cancellationPolicy} />
                        </div>
                      </div>
                    </div>
                    <div className=" justify-end px-4 py-3 bg-white dark:bg-neutral-900 border-t border-neutral-200 dark:border-neutral-700 flex">
                      <ButtonPrimary
                        sizeClass="px-6 py-3 !rounded-lg"
                        onClick={() => {
                          setPrivacyPolicyChecked(true);
                          setIsCancellationOpen(false);
                        }}
                      >
                        Accept
                      </ButtonPrimary>
                    </div>
                  </>
                </DialogPanel>
              </Transition>
            </div>
          </div>
        </Dialog>
      </Transition>
      <Transition appear show={isCustomAgreementOpen} as={Fragment}>
        <Dialog
          as="div"
          className="HeroSearchFormMobile__Dialog relative z-50"
          onClose={() => setIsCustomAgreementOpen(false)}
          static={true}
        >
          <div className="fixed inset-0 bg-neutral-100 dark:bg-neutral-900">
            <div className="flex h-full">
              <Transition
                as={Fragment}
                enter="ease-out transition-transform"
                enterFrom="opacity-0 translate-y-52"
                enterTo="opacity-100 translate-y-0"
                leave="ease-in transition-transform"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-52"
              >
                <DialogPanel className="relative h-full w-full overflow-hidden flex-1 flex flex-col justify-between ">
                  <>
                    <div className="absolute left-4 top-4">
                      <button
                        className="focus:outline-none focus:ring-0"
                        onClick={() => setIsCustomAgreementOpen(false)}
                      >
                        <XMarkIcon className="w-5 h-5 text-gray-700 dark:text-gray-300 " />
                      </button>
                    </div>
                    <div className="flex-1 pt-12 p-1 flex flex-col overflow-auto">
                      <div className="flex-1 flex flex-col bg-bg-color z-10">
                        <div className="flex-1 flex flex-col transition-opacity animate-[myblur_0.4s_ease-in-out] overflow-auto">
                          {agreementContent?.iframeUrl && (
                            <iframe
                              src={agreementContent.iframeUrl}
                              title="Rental Agreement"
                              style={{
                                width: "100%",
                                height: "100%",
                                border: "none",
                                minHeight: 600,
                              }}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="justify-end px-4 py-3 bg-bg-color border-t  flex">
                      <ButtonPrimary
                        sizeClass="px-6 py-3 !rounded-md"
                        onClick={() => {
                          setTermsandConditionChecked(true);
                          setIsCustomAgreementOpen(false);
                        }}
                      >
                        Accept
                      </ButtonPrimary>
                    </div>
                  </>
                </DialogPanel>
              </Transition>
            </div>
          </div>
        </Dialog>
      </Transition>

      <Transition appear show={isRentalAgreementPdfOpen} as={Fragment}>
        <Dialog
          as="div"
          className="HeroSearchFormMobile__Dialog relative z-50"
          onClose={() => setIsRentalAgreementPdfOpen(false)}
          static={true}
        >
          <div className="fixed inset-0 bg-neutral-100 dark:bg-[#121212]">
            <div className="flex h-full">
              <Transition
                as={Fragment}
                enter="ease-out transition-transform"
                enterFrom="opacity-0 translate-y-52"
                enterTo="opacity-100 translate-y-0"
                leave="ease-in transition-transform"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-52"
              >
                <DialogPanel className="relative h-full w-full overflow-hidden flex-1 flex flex-col justify-between">
                  <>
                    <div className="absolute left-4 top-4">
                      <button
                        className="focus:outline-none focus:ring-0"
                        onClick={() => setIsRentalAgreementPdfOpen(false)}
                      >
                        <XMarkIcon className="w-5 h-5 text-black dark:text-white" />
                      </button>
                    </div>
                    <div className="flex-1 pt-12 p-1 flex flex-col overflow-auto">
                      <div className="flex-1 flex flex-col bg-white dark:bg-[#121212] z-10">
                        <div className="flex-1 flex flex-col transition-opacity animate-[myblur_0.4s_ease-in-out] overflow-auto">
                          {rentalAgreementPdfUrl && (
                            <iframe
                              src={rentalAgreementPdfUrl}
                              title="Rental Agreement"
                              style={{
                                width: "100%",
                                height: "100%",
                                border: "none",
                                minHeight: 600,
                              }}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="justify-end px-4 py-3 bg-white dark:bg-[#121212] border-t border-neutral-200 dark:border-[#E5E7EB] flex">
                      <ButtonPrimary
                        sizeClass="px-6 py-3 !rounded-lg"
                        onClick={() => {
                          setRentalAgreementPdfChecked(true);
                          setRentalAgreementChecked(true);
                          setIsRentalAgreementPdfOpen(false);
                        }}
                      >
                        Accept
                      </ButtonPrimary>
                    </div>
                  </>
                </DialogPanel>
              </Transition>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default CheckOutPagePageMain;
